import * as Sentry from "@sentry/browser";
import { Spin, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  APIError,
  Department,
  FormFieldConfig,
  Team,
  User,
} from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  getUniqueArrayObjects,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import { ALL_USERS_QUERY } from "../gql/users";
import { GET_DEPARTMENTS } from "../gql/departments";
import { GET_TEAM, UPDATE_TEAM } from "../gql/teams";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface RouteParams {
  id?: string;
}

interface State {
  id?: string;
  name: string;
  users: User[];
  isLoading: boolean;
  departments: Department[];
  team?: Team;
  userSearchTerm: string;
}

export default class EditTeam extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      name: "",
      users: [],
      isLoading: true,
      departments: [],
      team: undefined,
      userSearchTerm: "",
    };

    this.reference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  componentDidMount(): void {
    this.setState(
      { id: this.props.match.params.id ?? "", isLoading: true },
      (): void => {
        this.getAllDepartments();
        this.getAllUsers();
        this.getTeamData();
      },
    );
  }

  getAllDepartments = (): void => {
    request({
      query: GET_DEPARTMENTS,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        departments: data.departments,
        isLoading: false,
      });
    });
  };

  getAllUsers(): void {
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  }

  getTeamData = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_TEAM,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              team: data.data.team,
            });
          } else {
            message.error("Failed to get port data");
          }
        });
      },
    );
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  redirectToAllTeamsPage = (): void => {
    this.props.history.push("/teams");
  };

  updateTeam = async (): Promise<void> => {
    interface Response {
      data?: {
        updateTeam: {
          team: Team;
          errors: APIError;
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        "name",
        "departmentId",
        "userIds",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_TEAM,
        variables: { ...data, id: this.state.team?.id },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateTeam",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An error occurred. The team could not be created");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Team updated sucessfully");
        this.redirectToAllTeamsPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to create team - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  render(): JSX.Element | null {
    const { isLoading, users, departments, team } = this.state;

    const teamUsersValue = team?.users?.map((user) => user.id);
    const usersOption = getUniqueArrayObjects([
      ...users,
      ...(team?.users ?? []),
    ]);

    const fields: FormFieldConfig[] = [
      {
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        placeholder: "Name",
        initialValue: team?.name,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "departmentId",
        rule: "string",
        inputType: "select",
        required: false,
        label: "Department",
        placeholder: "Department",
        selectOptions: departments,
        initialValue: team?.department?.id,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        name: "userIds",
        inputType: "multipleSelectSearch",
        rule: "string",
        required: true,
        label: "Users",
        placeholder: "Users",
        selectOptions: usersOption,
        searchHandler: this.handleUserSearchInput,
        isArrayField: true,
        initialValue: teamUsersValue,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "updateTeam",
        name: "updateTeamButton",
        inputType: "button",
        value: "Update",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "20px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateTeam,
      },
    ];

    return (
      <Card>
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
